import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Stack, Switch, Typography } from '@mui/material';

import BonusIcon from 'components/common/icons/Bonus';
import RealWalletIcon from 'components/common/icons/RealWallet';

import useBonusBalance from 'store/bonusBalance';
import useDialog from 'store/dialog';
import useHideBalance from 'store/hideBalance';
import useRealBalance from 'store/realBalance';

import { priceFormatter } from 'utils/price';
import useTranslates from 'utils/translate';
import useScreen from 'hooks/useScreen';

import useSettings, { IWebsiteConfigs } from 'store/settings';
import sx from './styles';
import useMount from 'hooks/useMount';

interface UserWalletProps {
  handleClose: () => void;
}

const BlurredBalance = () => <span style={{ filter: 'blur(4px)' }}>00000000</span>;

const HIDE_ENABLE_PAYING_FROM_BONUS_WALLET = false;

const Balance: FC<UserWalletProps> = ({ handleClose }) => {
  const navigate = useNavigate();
  const { isMobile } = useScreen();
  const { translate } = useTranslates();
  const [, dialogActions] = useDialog();
  const [
    {
      data: { meta = {} as IWebsiteConfigs },
    },
  ] = useSettings();
  const [hideBalance, { toggle: hideBalanceToggle }] = useHideBalance();

  const [bonusBalanceState, bonusBalanceActions] = useBonusBalance();
  const [realBalanceState, realBalanceActions] = useRealBalance();

  const { amount: userBalance } = realBalanceState;

  useMount(() => {
    bonusBalanceActions.read();
    realBalanceActions.read();
  });

  return (
    <Box>
      <Box sx={{ ...sx.menuHead, px: 0, pb: 0 }}>
        <Box width="100%" display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="column">
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              <Switch checked={hideBalance} onClick={hideBalanceToggle} />
              {translate('hide_balance')}
            </Box>
            {HIDE_ENABLE_PAYING_FROM_BONUS_WALLET && (
              <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                <Switch
                  checked={realBalanceState.enablePayingFromBonusWallet}
                  onClick={() => realBalanceActions.enablePayingFromBonusWallet()}
                />
                {translate('enable_paying_from_bonus_wallet')}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box px={3} py={2}>
        <Box display="flex" justifyContent="space-between">
          <Box
            sx={({ palette }) => ({ ...sx.menuInfo, fill: palette.text.primary })}
            gap={1}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
          >
            <Stack gap={1} direction="row">
              <RealWalletIcon sx={{ fontSize: 30 }} />
              <Box ml={1}>
                <Typography fontSize={12} fontWeight={600} sx={{ opacity: 0.5 }}>
                  {translate('wallet')}
                </Typography>
                <Typography fontSize={14} fontWeight={600}>
                  {hideBalance ? <BlurredBalance /> : priceFormatter(userBalance)}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Stack direction="row" gap={1} sx={({ palette }) => ({ ...sx.menuInfo, fill: palette.primary.main })}>
            <BonusIcon sx={{ fontSize: 30 }} />
            <Box>
              <Typography fontSize={12} fontWeight={600} sx={{ opacity: 0.5 }}>
                {translate('bonus')}
              </Typography>
              <Typography fontSize={14} fontWeight={600} color="primary.main">
                {hideBalance ? <BlurredBalance /> : priceFormatter(bonusBalanceState.amount)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
      {(meta.showWithdraw || meta.showDeposit) && (
        <Box display="flex" gap={2} justifyContent="center" mb={2}>
          {meta.showWithdraw && (
            <Button
              size="large"
              variant="contained"
              color="secondary"
              sx={{ flexGrow: 1 }}
              onClick={() => {
                isMobile ? navigate('/withdraw') : dialogActions.set('withdraw');
                handleClose();
              }}
            >
              {translate('withdraw')}
            </Button>
          )}
          {meta.showDeposit && (
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={() => {
                isMobile ? navigate('/deposit') : dialogActions.set('deposit');
                handleClose();
              }}
              sx={{ flexGrow: 1 }}
            >
              {translate('deposit')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Balance;
